package ai.jessie.web.documents

import ai.jessie.shared.documents.DocumentDetailResponse
import ai.jessie.shared.documents.DocumentProblemResponse
import ai.jessie.web.onboarding.OnboardingProblem
import ai.jessie.web.onboarding.unavailableProblem

// How a refused request changes the shown processing: the next state, a session or organisation
// loss for the page (UC-006), and what must be reloaded.
internal class DocumentRefusal(
    val state: DocumentWorkflowUiState,
    val lost: OnboardingProblem? = null,
    val reloadHistory: Boolean = false,
    val refreshChoices: Boolean = false,
)

// One stable message per alternative flow, without hidden detail. The state is shown busy until
// the request finishes.
internal fun DocumentWorkflowUiState.refusedBy(
    failure: DocumentRequestFailure,
    writing: Boolean,
): DocumentRefusal {
    val detail = failure.detail
    val corrections =
        detail.problems.associate {
            it.field to documentFieldCorrection(it.field, it.reason, this.detail)
        }
    return when {
        // UC-006: the session, role or organisation itself is gone; the page shows it.
        failure.status == HTTP_UNAUTHENTICATED ||
            failure.status == HTTP_FORBIDDEN ||
            detail.code == "ORGANISATION_REQUIRED" ->
            DocumentRefusal(DocumentWorkflowUiState(busy = true), lost = failure.loadProblem())
        // A4: hidden, foreign or removed; no detail stays.
        failure.status == HTTP_NOT_FOUND ->
            DocumentRefusal(
                DocumentWorkflowUiState(busy = true, error = unavailableDocumentProblem())
            )
        // A14 and A2: the current document replaces the shown one.
        detail.code == "DOCUMENT_CHANGED" || detail.code == "TRANSITION_NOT_PERMITTED" ->
            stale(detail)
        // A3: the edits stay; the current active choices replace the shown ones.
        detail.code == "CHOICE_UNAVAILABLE" ->
            DocumentRefusal(
                copy(
                    confirmation = null,
                    problems = corrections,
                    error = correctionProblem(corrections.keys, detail.code),
                ),
                refreshChoices = true,
            )
        // A5, B7 and step 6: the marked fields; the workflow state is unchanged.
        detail.code in FIELD_REFUSALS ->
            DocumentRefusal(
                copy(
                    confirmation = null,
                    problems = corrections,
                    error = correctionProblem(corrections.keys, detail.code),
                )
            )
        // A6, A9-A11: the confirmation stays open with its input marked.
        detail.code in INPUT_REFUSALS ->
            DocumentRefusal(
                copy(
                    problems = corrections,
                    error = correctionProblem(corrections.keys, detail.code),
                )
            )
        // A15: nothing was saved; the confirmation stays open for another attempt.
        failure.status == 0 || failure.status >= HTTP_SERVER_ERROR ->
            DocumentRefusal(copy(error = failedProblem(failure.status, writing)))
        else -> DocumentRefusal(copy(confirmation = null, error = refusalProblem(detail.code)))
    }
}

private fun DocumentWorkflowUiState.stale(problem: DocumentProblemResponse): DocumentRefusal {
    val current = problem.document ?: detail
    val newer = current?.version != detail?.version
    return DocumentRefusal(
        copy(
            detail = current,
            draft = if (newer) current?.details else draft,
            confirmation = null,
            error = staleProblem(problem.code, current),
        ),
        reloadHistory = newer,
    )
}

private fun failedProblem(status: Int, writing: Boolean) =
    when {
        !writing -> unavailableProblem()
        status == 0 -> uncertainProblem()
        else -> notCompletedProblem()
    }

internal fun staleProblem(code: String, current: DocumentDetailResponse?): OnboardingProblem {
    val cause =
        if (code == "DOCUMENT_CHANGED")
            "Das Dokument wurde inzwischen geändert. Es wurde nichts überschrieben; bitte prüfe " +
                "die aktuellen Angaben."
        else "Dieser Statuswechsel ist nicht zulässig. Es wurde nichts geändert."
    val permitted = current?.let { detail ->
        if (detail.transitions.isEmpty()) " Für dich ist kein Statuswechsel möglich."
        else
            " Mögliche Schritte: " +
                detail.transitions.joinToString(", ") { transitionLabel(detail.status, it) } +
                "."
    }
    return OnboardingProblem(cause + permitted.orEmpty())
}

private fun correctionProblem(fields: Set<String>, code: String): OnboardingProblem {
    val message =
        when (code) {
            "ARCHIVE_INCOMPLETE" ->
                "Für die Archivierung fehlen Angaben. Das Dokument wurde nicht archiviert."
            "INVALID_PAYMENT_DATE" ->
                "Das Zahlungsdatum ist nicht zulässig. Die Zahlung wurde nicht geändert."
            "INVALID_REASON" -> "Bitte gib einen Grund an. Es wurde nichts geändert."
            else -> "Bitte korrigiere die markierten Angaben. Es wurde nichts gespeichert."
        }
    return OnboardingProblem(message, fields.map(::documentInputId))
}

private fun refusalProblem(code: String) =
    OnboardingProblem(
        when (code) {
            "RELEVANCE_LOCKED" ->
                "Die Buchhaltungsrelevanz kann jetzt nicht entfernt werden: Das Dokument ist in " +
                    "der Buchhaltung, mit einer Rechnung verknüpft oder unterliegt der " +
                    "Aufbewahrungspflicht. Es wurde nichts geändert."
            "LINKED_INVOICE_NOT_SENT" ->
                "Die verknüpfte Rechnung muss versendet sein, bevor Dokument und Rechnung als " +
                    "bezahlt gelten. Es wurde nichts geändert."
            "LINKED_PAYMENT_IMMUTABLE" ->
                "Die Zahlung ist mit einer bezahlten Rechnung geteilt. Bezahlte Rechnungen sind " +
                    "endgültig (UC-014), deshalb kann die Zahlung nicht geändert werden."
            "PAYMENT_MISSING" -> "Es ist keine Zahlung erfasst. Es wurde nichts geändert."
            "DOCUMENT_READ_ONLY" ->
                "Archivierte und abgelehnte Dokumente sind schreibgeschützt, bis sie reaktiviert " +
                    "werden. Es wurde nichts geändert."
            "REMOVAL_NOT_ELIGIBLE" ->
                "Dieses Dokument kann nicht endgültig gelöscht werden. Es bleibt unverändert."
            else -> "Die Anfrage konnte nicht verarbeitet werden. Es wurde nichts geändert."
        }
    )

internal fun notCompletedProblem() =
    OnboardingProblem(
        "Die Verarbeitung wurde nicht abgeschlossen. Status und gespeicherte Angaben sind " +
            "unverändert. Bitte versuche es erneut."
    )

internal fun uncertainProblem() =
    OnboardingProblem(
        "Die Antwort konnte nicht bestätigt werden. Bitte lade das Dokument neu, um den " +
            "gespeicherten Stand zu prüfen."
    )

// A problem field marks its form control; reason and payment date are the confirmation's input.
internal fun documentInputId(field: String) =
    if (field == "reason" || field == PAID_ON) CONFIRMATION_INPUT_ID else "document-field-$field"

private val FIELD_REFUSALS = setOf("ARCHIVE_INCOMPLETE", "INVALID_DETAILS", "FIELD_NOT_EDITABLE")
private val INPUT_REFUSALS = setOf("INVALID_REASON", "INVALID_PAYMENT_DATE")
internal const val CONFIRMATION_INPUT_ID = "document-workflow-input"
private const val HTTP_UNAUTHENTICATED = 401
private const val HTTP_FORBIDDEN = 403
private const val HTTP_NOT_FOUND = 404
private const val HTTP_SERVER_ERROR = 500
